import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useContext } from "react";
import { Button, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Link, useParams } from "react-router-dom";

import { createCoreNetworkWorkRequest, listCoreNetworkWorkRequests } from "../../../services/coreNetwork";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import {
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  LoadingState,
  PagedTable,
  SectionCard,
  StatusChip,
  buildCoreNetworkPath,
  formatCurrency,
  formatDateTime,
  safeArray,
  toTitleCase,
} from "./coreNetworkUiShared";
import CoreNetworkWorkRequestForm from "./components/CoreNetworkWorkRequestForm";

export default function CoreNetworkWorkRequestList() {
  const { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", requestType: "", fulfillmentProvider: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [workRequests, setWorkRequests] = useState([]);
  const canCreateRequest = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.CREATE_REQUEST, instanceUniqueId);
  const canManagePayment = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT, instanceUniqueId);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await listCoreNetworkWorkRequests({
        instanceUniqueId,
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.requestType ? { requestType: filters.requestType } : {}),
        ...(filters.fulfillmentProvider ? { fulfillmentProvider: filters.fulfillmentProvider } : {}),
      });
      setWorkRequests(safeArray(response.workRequests));
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || "Unable to load work requests.");
    } finally {
      setLoading(false);
    }
  }, [filters.fulfillmentProvider, filters.requestType, filters.status, instanceUniqueId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const sortedRequests = useMemo(
    () => [...workRequests].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [workRequests]
  );

  const setFilter = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreate = async (form) => {
    try {
      setSaving(true);
      await createCoreNetworkWorkRequest({
        ...form,
        instanceUniqueId,
      });
      setDialogOpen(false);
      await loadRequests();
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError.message || "Unable to create work request.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CoreNetworkShell
      title="Core Network Work Requests"
      breadcrumb={`${CORE_NETWORK_LABEL} / Work Requests`}
    >
      <Stack spacing={3}>
        {loading ? <LoadingState label="Loading work requests..." /> : null}
        {!loading && error ? <ErrorState message={error} onAction={loadRequests} /> : null}
        <SectionCard
          title="Request Queue"
          subtitle="View all Core Network work requests across Elite Core instances, plus internal and future provider flows."
          action={canCreateRequest ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Create work request
            </Button>
          ) : null}
        >
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth select label="Status" value={filters.status} onChange={setFilter("status")}>
                <MenuItem value="">All</MenuItem>
                {["submitted", "matched", "offered", "assigned", "accepted", "in_progress", "awaiting_review", "revision_requested", "ready", "completed", "cancelled"].map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Request type" value={filters.requestType} onChange={setFilter("requestType")} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth select label="Provider" value={filters.fulfillmentProvider} onChange={setFilter("fulfillmentProvider")}>
                <MenuItem value="">All</MenuItem>
                {["core-network", "internal", "binated"].map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ height: "100%", alignItems: "center" }}>
                <Button variant="outlined" onClick={loadRequests}>Refresh</Button>
              </Stack>
            </Grid>
          </Grid>
          {!loading ? (
            <PagedTable
              rows={sortedRequests}
              columns={[
                {
                  key: "title",
                  label: "Request",
                  render: (row) => (
                    <Stack spacing={0.5}>
                      <Typography
                        component={Link}
                        to={buildCoreNetworkPath(instanceUniqueId, `work-requests/${row.id}`)}
                        sx={{ fontWeight: 700, textDecoration: "none", color: "#103a71" }}
                      >
                        {row.title || `Work Request #${row.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toTitleCase(row.requestType)}
                      </Typography>
                    </Stack>
                  ),
                },
                { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
                { key: "providerStatusSummary", label: "Provider Status", render: (row) => row.providerStatusSummary ? <StatusChip value={row.providerStatusSummary} /> : "-" },
                { key: "fulfillmentProvider", label: "Provider", render: (row) => toTitleCase(row.fulfillmentProvider) },
                { key: "networkType", label: "Network", render: (row) => toTitleCase(row.networkType) },
                ...(canManagePayment ? [{ key: "budgetAmount", label: "Budget", render: (row) => formatCurrency(row.budgetAmount, row.budgetCurrency || "CAD") }] : []),
                { key: "deadlineAt", label: "Deadline", render: (row) => formatDateTime(row.deadlineAt) },
              ]}
              emptyTitle="No work requests found"
              emptyDescription="Create a work request to start the internal Core Network flow."
            />
          ) : null}
        </SectionCard>

        {canCreateRequest ? (
          <CoreNetworkWorkRequestForm
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onSubmit={handleCreate}
            submitting={saving}
            canManagePayment={canManagePayment}
          />
        ) : null}
      </Stack>
    </CoreNetworkShell>
  );
}
